import { useField, useFormikContext } from 'formik';

import styled from '@emotion/styled';

import {
  FieldErrorInfo,
  IInputField,
  Input,
  InputContainer,
  InputLabel,
  InputWrapper,
} from './FormField';

export const DisabledCurrencyOnlyInputField: React.FC<
  IInputField & { maxLength?: number; showErrorLabel?: boolean }
> = ({
  label,
  lpiSrc,
  rpiSrc,
  className,
  maxLength,
  showErrorLabel = true,
  onValueChange,
  readonly = false,
  ...props
}) => {
  const [field, meta] = useField(props);
  const { setFieldValue } = useFormikContext<any>();

  // Format number with thousand separators
  const formatMoney = (value: string | number): string => {
    if (!value) return '';
    const numStr = value.toString().replace(/,/g, '');
    return numStr.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // Remove formatting to get raw number
  const unFormatMoney = (value: string): string => {
    return value.replace(/,/g, '');
  };

  const handleChange = (e: any) => {
    const { value } = e.target;
    const rawValue = unFormatMoney(value);

    if (/^\d*$/.test(rawValue)) {
      // Check if maxLength is specified and enforce the limit
      if (maxLength && rawValue.length > maxLength) {
        return; // Don't update if exceeding max length
      }
      setFieldValue(props.name, rawValue);
      if (onValueChange) {
        onValueChange(rawValue, props.name);
      }
    } else if (rawValue === '') {
      setFieldValue(props.name, '');
      if (onValueChange) {
        onValueChange('', props.name);
      }
    }
  };

  return (
    <InputWrapper className={className}>
      <WrappedInputLabel htmlFor={props.name || props.id}>
        {label}
      </WrappedInputLabel>
      <InputContainer>
        <WrappedInput
          {...field}
          {...props}
          readOnly={readonly}
          value={formatMoney(field?.value ?? '')}
          onChange={(e) => handleChange(e)}
          padding='10px 40px'
        />
        <CurrencyIcon>₦</CurrencyIcon>
        <AutoCalculateText>Auto-calculated</AutoCalculateText>
      </InputContainer>
      {showErrorLabel && meta.touched && meta.error ? (
        <FieldErrorInfo>{meta.error.capitalize()}</FieldErrorInfo>
      ) : null}
    </InputWrapper>
  );
};

export default DisabledCurrencyOnlyInputField;

const CurrencyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: auto;
  margin-left: 16px;
  color: #5b91f5;
  /* border: 1px solid red; */
`;

const WrappedInputLabel = styled(InputLabel)`
  color: #6b7280;
  font-weight: 600;
  font-size: 12px;
`;

const WrappedInput = styled(Input)`
  cursor: default;
  color: #6e7079;
  font-weight: 600;
  background-color: #e0e0e0;
  /* color: ${({ theme }) => theme.palette.blueColor}; */

  &::placeholder {
    font-weight: 600;
    color: ${({ theme }) => theme.palette.blueColor};
  }
`;

const AutoCalculateText = styled.div`
  font-weight: 500;
  font-size: 12px;
  line-height: 12px;
  color: #6e7079;
  margin-left: auto;
  margin-right: 16px;
  margin-top: 13px;
  cursor: default;
  user-select: none;
`;
